import type { KioskLanguage } from "./types";

export const hasVisibleContent = (html?: string | null): boolean => {
  if (!html) return false;
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&[a-z]+;|&#\d+;/gi, " ")
    .trim();
  return text.length > 0;
};

export const ITEMS_CLOSED_NOTICE =
  "One or more items you selected are no longer accepting speakers and have been removed from your selections.";

export const TRANSLATE_UNAVAILABLE_NOTICE =
  "Translation isn't available right now. Please ask the meeting clerk for language help.";

export const STEPS = [
  { key: "welcome", label: "Welcome" },
  { key: "pick", label: "Pick items" },
  { key: "info", label: "Your info" },
  { key: "review", label: "Review" }
] as const;

export type StepKey = (typeof STEPS)[number]["key"];

export const TIME_MARKERS = new Set([
  "AM",
  "PM",
  "ET",
  "PT",
  "CT",
  "MT",
  "EST",
  "EDT",
  "CST",
  "CDT",
  "MST",
  "MDT",
  "PST",
  "PDT",
  "HST",
  "AKST",
  "AKDT",
  "UTC",
  "GMT"
]);

export const ACRONYM_MAX_LEN = 4;

export const WIN_TZ_TO_IANA: Record<string, string> = {
  "Pacific Standard Time": "America/Los_Angeles",
  "Mountain Standard Time": "America/Denver",
  "US Mountain Standard Time": "America/Phoenix",
  "Central Standard Time": "America/Chicago",
  "Eastern Standard Time": "America/New_York",
  "Atlantic Standard Time": "America/Halifax",
  "Hawaiian Standard Time": "Pacific/Honolulu",
  "Alaskan Standard Time": "America/Anchorage",
  "Aleutian Standard Time": "America/Adak",
  UTC: "UTC",
  "GMT Standard Time": "Europe/London"
};

export const maskEmail = (email: string): string => {
  if (!email) return "";
  const at = email.indexOf("@");
  if (at < 1) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const lastDot = domain.lastIndexOf(".");
  const head = local.charAt(0) + "•••";
  if (lastDot < 1) {
    return `${head}@${domain.charAt(0)}•••`;
  }
  const domainName = domain.slice(0, lastDot);
  const tld = domain.slice(lastDot);
  return `${head}@${domainName.charAt(0)}•••${tld}`;
};

export const maskPhone = (phone: string): string => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 4) return phone;
  return `(•••) •••-${digits.slice(-4)}`;
};

export function isPlausibleEmail(s: string): boolean {
  if (!s) return false;
  const trimmed = s.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

export function isPlausiblePhone(s: string): boolean {
  if (!s || !s.trim()) return true;
  const digits = s.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
}

export function formatPhone(digits: string): string {
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(
    4,
    7
  )}-${digits.slice(7)}`;
}

export const isLikelyAcronym = (w: string): boolean => {
  if (!w) return false;
  if (TIME_MARKERS.has(w)) return true;
  return w.length <= ACRONYM_MAX_LEN && /^\p{Lu}+$/u.test(w);
};

export function normalizeShoutyCaps(input: string | null | undefined): string {
  if (!input) return "";
  const s = String(input).trim();
  if (s.length === 0) return "";
  if (/\p{Ll}/u.test(s)) return s;
  if (s.length <= 3 && !/\s/.test(s)) return s;
  const smallWords = new Set([
    "a",
    "an",
    "and",
    "as",
    "at",
    "but",
    "by",
    "en",
    "for",
    "if",
    "in",
    "of",
    "on",
    "or",
    "the",
    "to",
    "v",
    "vs",
    "via",
    "with",
    "de",
    "del",
    "el",
    "la",
    "las",
    "los",
    "y",
    "o",
    "u",
    "en",
    "por",
    "para"
  ]);
  const words = s.split(/(\s+|-)/);
  let lastNonSep = -1;
  for (let i = words.length - 1; i >= 0; i--) {
    if (!/^\s+$/.test(words[i]) && words[i] !== "-") {
      lastNonSep = i;
      break;
    }
  }
  return words
    .map((word, idx) => {
      if (/^\s+$/.test(word) || word === "-") return word;
      if (isLikelyAcronym(word)) return word;
      const lower = word.toLowerCase();
      const isFirst =
        idx === 0 ||
        words.slice(0, idx).every((w) => /^\s+$/.test(w) || w === "-");
      const isLast = idx === lastNonSep;
      if (!isFirst && !isLast && smallWords.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join("");
}

export function windowsToIana(
  winTz: string | null | undefined
): string | undefined {
  if (!winTz) return undefined;
  const trimmed = winTz.trim();
  if (!trimmed) return undefined;
  if (trimmed.includes("/")) return trimmed;
  return WIN_TZ_TO_IANA[trimmed];
}

export function formatMeetingWhen(
  iso: string | null | undefined,
  winTimeZone?: string | null
): string {
  if (!iso) return "Upcoming";
  const normalized = /[Z]$|[+-]\d{2}:?\d{2}$/.test(iso.trim())
    ? iso
    : `${iso}Z`;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return "Upcoming";
  const iana = windowsToIana(winTimeZone);
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short"
  };
  if (iana) opts.timeZone = iana;
  return d.toLocaleString([], opts).toUpperCase();
}

export function currentLanguageLabel(
  currentCode: string,
  langs: KioskLanguage[]
): string {
  if (!langs || langs.length === 0) return "English";
  const match = langs.find(
    (l) => l.code.toLowerCase() === (currentCode || "en").toLowerCase()
  );
  if (match) return match.autoglossonym || match.name || match.code;
  return "Languages";
}

export type TranslateOutcome = "applied" | "superseded" | "unavailable";

let latestTranslationRequest = 0;

export function applyTranslateLanguage(
  code: string
): Promise<TranslateOutcome> {
  if (typeof document === "undefined") return Promise.resolve("unavailable");

  const request = ++latestTranslationRequest;
  const target = (code || "en").toLowerCase();
  const revertingToEnglish = target === "en";

  writeGoogTransCookie(target);

  const stage = document.querySelector(".stage") as HTMLElement | null;
  if (stage && !revertingToEnglish) stage.classList.add("mk-retranslating");

  const clearOverlay = (delayMs: number) => {
    if (!stage) return;
    window.setTimeout(() => stage.classList.remove("mk-retranslating"), delayMs);
  };

  return new Promise<TranslateOutcome>((resolve) => {
    let attempts = 0;

    const apply = () => {
      if (request !== latestTranslationRequest) {
        resolve("superseded");
        return;
      }

      attempts += 1;
      const combo = document.querySelector(
        "select.goog-te-combo"
      ) as HTMLSelectElement | null;

      if (combo && translateOptionAvailable(combo, target)) {
        combo.value = target;
        if (translateSelectionApplied(combo.value, target)) {
          combo.dispatchEvent(new Event("change", { bubbles: true }));
          clearOverlay(revertingToEnglish ? 50 : 400);
          resolve("applied");
          return;
        }
      }

      if (attempts < TRANSLATE_APPLY_ATTEMPTS) {
        window.setTimeout(apply, TRANSLATE_APPLY_RETRY_MS);
        return;
      }

      clearOverlay(0);
      resolve("unavailable");
    };

    apply();
  });
}

function writeGoogTransCookie(target: string): void {
  const host = (() => {
    try {
      return window.location.hostname.replace(/^www\./, "");
    } catch {
      return "";
    }
  })();

  if (target === "en") {
    document.cookie = "googtrans=; path=/; Max-Age=0";
    if (host) document.cookie = `googtrans=; path=/; domain=.${host}; Max-Age=0`;
    return;
  }

  const value = `/en/${target}`;
  document.cookie = `googtrans=${value}; path=/`;
  if (host) document.cookie = `googtrans=${value}; path=/; domain=.${host}`;
}

export const TRANSLATE_APPLY_ATTEMPTS = 30;
export const TRANSLATE_APPLY_RETRY_MS = 200;
export const RETRANSLATE_PASSES = 3;
export const RETRANSLATE_PASS_MS = 250;
export const RETRANSLATE_DEBOUNCE_MS = 120;

/** Rendered inside the stage and re-mounted per screen, so its state answers "is what is on
 *  screen right now actually translated?" rather than "did we once ask for a translation?". */
export const TRANSLATE_PROBE_TEXT = "Checking translation";
export const TRANSLATE_PROBE_SELECTOR = ".translate-probe";

export function translationLanded(target: string): boolean {
  if ((target || "en").toLowerCase() === "en") return true;

  const probe = document.querySelector(TRANSLATE_PROBE_SELECTOR);
  if (!probe) return true;

  return (probe.textContent ?? "").trim() !== TRANSLATE_PROBE_TEXT;
}

/**
 * Re-applies the current language to whatever React just rendered, verifies it landed, and
 * escalates if it did not. Yields to any language the resident picks while it is running, and
 * shows no transition overlay — the screen already changed.
 */
export function retranslateCurrentLanguage(code: string): void {
  if (typeof document === "undefined") return;

  const target = (code || "en").toLowerCase();
  if (target === "en") return;

  const startedAt = latestTranslationRequest;
  let pass = 0;

  const nudge = () => {
    if (startedAt !== latestTranslationRequest) return;
    if (translationLanded(target)) return;

    const combo = document.querySelector(
      "select.goog-te-combo"
    ) as HTMLSelectElement | null;

    if (combo && translateOptionAvailable(combo, target)) {
      // Re-selecting the value the widget already holds is a no-op for it, so once a pass has
      // gone by without the probe changing, bounce through the original language to force a
      // fresh run. Nothing flashes: this only happens while the screen is still untranslated.
      if (pass > 0 && translateSelectionApplied(combo.value, target)) {
        selectTranslateValue(combo, "en");
      }
      selectTranslateValue(combo, target);
    }

    pass += 1;
    if (pass < RETRANSLATE_PASSES) window.setTimeout(nudge, RETRANSLATE_PASS_MS);
  };

  nudge();
}

function selectTranslateValue(combo: HTMLSelectElement, value: string): void {
  combo.value = value;
  if (!translateSelectionApplied(combo.value, value)) return;
  combo.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * The widget creates `select.goog-te-combo` before it fills in the option list, and assigning a
 * value with no matching option is silently ignored — so the select existing is not enough.
 */
export function translateOptionAvailable(
  combo: { options?: ArrayLike<{ value: string }> },
  target: string
): boolean {
  const options = Array.from(combo.options ?? []);
  if (options.length === 0) return false;

  const wanted = (target || "en").toLowerCase();

  return options.some((option) => {
    const value = (option.value || "").toLowerCase();
    return wanted === "en" ? value === "" || value === "en" : value === wanted;
  });
}

/** Google Translate reports the original language as either "en" or an empty value. */
export function translateSelectionApplied(
  comboValue: string,
  target: string
): boolean {
  const wanted = (target || "en").toLowerCase();
  const actual = (comboValue || "").toLowerCase();

  if (wanted === "en") return actual === "" || actual === "en";

  return actual === wanted;
}
